import { lazy, Suspense, useEffect, useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Services from './components/Services';
import Activities from './components/Activities';
import About from './components/About';
import Safety from './components/Safety';
import HowItWorks from './components/HowItWorks';
import Sales from './components/Sales';
import Gallery from './components/Gallery';
import Contact from './components/Contact';
import Footer from './components/Footer';

const Admin = lazy(() => import('./components/Admin'));
const ActivitiesPage = lazy(() => import('./components/ActivitiesPage'));
const SafetyPage = lazy(() => import('./components/SafetyPage'));
const IntegritetPage = lazy(() => import('./components/IntegritetPage'));
const CookiesPage = lazy(() => import('./components/CookiesPage'));

type Route = 'home' | 'activities' | 'admin' | 'sakerhet' | 'integritet' | 'cookies';

function normalizeAdminPath(raw?: string): string {
  if (!raw) return '__admin_path_not_configured__';
  const trimmed = raw.trim();
  if (!trimmed) return '__admin_path_not_configured__';
  return '/' + trimmed.replace(/^\/|\/+$/g, '');
}

// Cloudflare Pages supports build-time env vars. Accept both `admin` and `/admin`.
const ADMIN_PATH = normalizeAdminPath(import.meta.env.VITE_ADMIN_PATH);

function routeFor(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (clean === ADMIN_PATH) return 'admin';
  if (clean === '/aktiviteter' || clean.startsWith('/aktiviteter/')) return 'activities';
  if (clean === '/sakerhet') return 'sakerhet';
  if (clean === '/integritet') return 'integritet';
  if (clean === '/cookies') return 'cookies';
  return 'home';
}

export default function App() {
  const [route, setRoute] = useState<Route>(() =>
    typeof window === 'undefined' ? 'home' : routeFor(window.location.pathname),
  );

  useEffect(() => {
    const onPop = () => setRoute(routeFor(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // After the home page mounts, retry scrolling to the URL hash. The browser's
  // initial scroll attempt happens before React renders the section, so it fails.
  useEffect(() => {
    if (route !== 'home') return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    let active = true;
    const t = setTimeout(() => {
      if (!active) return;
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 100);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [route]);

  if (route !== 'home') {
    return (
      <Suspense fallback={<div className="min-h-screen bg-cream" />}>
        {route === 'admin' && <Admin />}
        {route === 'activities' && <ActivitiesPage />}
        {route === 'sakerhet' && <SafetyPage />}
        {route === 'integritet' && <IntegritetPage />}
        {route === 'cookies' && <CookiesPage />}
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-cream text-ink overflow-x-clip">
      <Navbar />
      <main>
        <Hero />
        <Services />
        <Activities />
        <Safety />
        <HowItWorks />
        <Sales />
        <Gallery />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
